import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Autocomplete,
  TextField,
  Stack,
  Chip,
} from "@mui/material";
import { Link, useSearchParams } from "react-router-dom";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import moment from "moment";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import TableFilter from "components/Shared/AppTable/TableFilter";
import AppTable from "components/Shared/AppTable";
import { useAuth, useTablePagination } from "hooks";
import {
  useLazyListCODChargeDeductReportQuery,
  useLazyGetDDLVendorListReportQuery,
} from "store/services/reportApi";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import ExportCODChargeDeductReport from "./ExportCODChargeDeductReport";

const CODChargeDeductReport = () => {
  const { viewEntity } = useAuth();
  const [searchParams] = useSearchParams();
  const keyword = searchParams.get("search") || "";
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [vendorIdFilter, setVendorIdFilter] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const [
    fetchReport,
    { data: reportRes, isLoading, isFetching },
  ] = useLazyListCODChargeDeductReportQuery();
  const [
    fetchVendors,
    { data: vendorData, isLoading: loadingVendors },
  ] = useLazyGetDDLVendorListReportQuery();

  useEffect(() => {
    if (viewEntity?.entity?._id) {
      fetchVendors({
        account: viewEntity?.entity?.account?._id,
        entity: viewEntity?.entity?._id,
        status: "Active",
      });
    }
  }, [fetchVendors, viewEntity]);

  useEffect(() => {
    fetchReport({
      account: viewEntity?.entity?.account?._id,
      entity: viewEntity?.entity?._id,
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
      ...(vendorIdFilter && { vendorId: vendorIdFilter }),
      ...(fromDate && { fromDate: moment(fromDate).format("MM-DD-YYYY") }),
      ...(toDate && { toDate: moment(toDate).format("MM-DD-YYYY") }),
    });
  }, [fetchReport, currentPage, perPage, keyword, vendorIdFilter, fromDate, toDate, viewEntity]);

  const vendorOptions = Array.isArray(vendorData?.data)
    ? vendorData.data.map((v) => ({ value: v.value, label: v.label }))
    : [];

  const columns = [
    {
      name: "COD Transfer ID",
      selector: (row) => row?.codTransferId,
      cell: (row) => (
        <Link to={`/app/crm/cod-transfer/${row?._id}/detail-view`}>
          {row?.codTransferId || "N/A"}
        </Link>
      ),
    },
    {
      name: "Vendor",
      selector: (row) => row?.vendorShopName,
      cell: (row) => (
        <div>
          <div>{row?.vendorShopName || "N/A"}</div>
          <div style={{ fontSize: "0.75rem", color: "#888" }}>
            {row?.vendorOwnerName || ""}
          </div>
        </div>
      ),
    },
    {
      name: "Actual COD Amount",
      selector: (row) => row?.actualAmount,
      cell: (row) => `Rs ${(row?.actualAmount || 0).toFixed(2)}`,
    },
    {
      name: "Service Charge (1%)",
      selector: (row) => row?.feeAmount,
      cell: (row) => `Rs ${(row?.feeAmount || 0).toFixed(2)}`,
    },
    {
      name: "Delivery Charge",
      selector: (row) => row?.deductChargeFromCOD,
      cell: (row) => `Rs ${(row?.deductChargeFromCOD || 0).toFixed(2)}`,
    },
    {
      name: "Transfer Amount",
      selector: (row) => row?.transferAmount,
      cell: (row) => `Rs ${(row?.transferAmount || 0).toFixed(2)}`,
    },
    {
      name: "Transfer Date",
      selector: (row) => row?.transferDate,
      cell: (row) => <AppTableDateTime date={row?.transferDate} />,
    },
  ];

  const breadcrumbs = [
    { title: "Reports", path: "/app/crm/reports/list" },
    { title: "COD Service Charge Deduct Report" },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <BreadcrumbLayout breadcrumbs={breadcrumbs}>
        <BreadcrumbLayout.Paper>
          <Box sx={{ p: 2 }}>
            <TableFilter
              searchLabel="COD Transfer ID"
              disabled={isLoading || isFetching}
              initialFilters={3}
              onClear={() => {
                setVendorIdFilter("");
                setFromDate(null);
                setToDate(null);
              }}
              exportComponent={ExportCODChargeDeductReport}
            >
              <DatePicker
                label="From Date"
                value={fromDate}
                onChange={(newValue) => setFromDate(newValue)}
                maxDate={toDate || new Date()}
                disableFuture
                renderInput={(params) => (
                  <TextField {...params} size="small" fullWidth />
                )}
              />
              <DatePicker
                label="To Date"
                value={toDate}
                onChange={(newValue) => setToDate(newValue)}
                minDate={fromDate}
                maxDate={new Date()}
                disableFuture
                renderInput={(params) => (
                  <TextField {...params} size="small" fullWidth />
                )}
              />
              <Autocomplete
                size="small"
                options={[
                  { value: "", label: "All Vendors" },
                  ...vendorOptions,
                ]}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) =>
                  option.value === value
                }
                value={
                  vendorOptions.find(
                    (opt) => opt.value === vendorIdFilter
                  ) || null
                }
                onChange={(e, newValue) =>
                  setVendorIdFilter(newValue?.value || "")
                }
                disableClearable
                disabled={loadingVendors}
                sx={{ minWidth: 200 }}
                renderInput={(params) => (
                  <TextField {...params} label="Vendor" />
                )}
              />
            </TableFilter>

            <AppTable
              columns={columns}
              data={reportRes?.data ?? []}
              progressPending={isLoading || isFetching}
              paginationServer
              paginationPerPage={perPage}
              paginationTotalRows={reportRes?.totalItems ?? 0}
              paginationDefaultPage={currentPage}
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handlePerRowsChange}
            />
          </Box>
        </BreadcrumbLayout.Paper>
      </BreadcrumbLayout>
    </LocalizationProvider>
  );
};

export default CODChargeDeductReport;